<template>
	<div
		class="pdf-signature"
		:class="{
			'pdf-signature-signed': overlay.signature.isSigned,
			'pdf-signature-cosigner': overlay.signature.personType == ENUMS.PERSON_TYPES.COCUSTOMER,
		}"
		:style="{
			height: '60px',
			width: calcWidth() + 'px',
			marginBottom: '4px',
			zIndex: overlay.signature.personType == ENUMS.PERSON_TYPES.CUSTOMER ? 2 : 1,
		}"
	>
		<img
			class="img-signature"
			v-if="overlay.signature.isSigned"
			:src="displayImage"
		/>
		<div
			v-else
			class="sign-name-container"
			@click="sign()"
		>
			<div class="signer-name">
				{{ displaySignerName }}
			</div>
			<div>Click To Sign</div>
		</div>
	</div>
</template>
<script lang="ts">
	export interface IPDFSignatureProps {
		overlay: IDocumentOverlay;
		pdfRef: HTMLElement;
	}
</script>
<script setup lang="ts">
	import { computed, inject } from 'vue';
	import { DigitalSignatureData } from '@core/classes/Paperwork/DigitalSignatureData';
	import { IDocumentOverlay } from '@core/classes/Paperwork/DocumentOverlay';
	import { Emitter } from 'mitt';
	import ENUMS from '@core/classes/Enums';
	import FIMenuCustomer from '@core/classes/FIMenuCustomer';
	import { getPersonSigningFromOverlay } from '@core/helpers/esign-helper';
	import settings from 'settings';
	import { useFIMenuForCustomerMeetingStore } from '@core/stores/FIMenuForCustomerMeetingStore';

	const $meetingHub = inject<any>('$meetingHub', null);
	const EventBus = inject<Emitter<any>>('EventBus', null);

	const fimenuStore = useFIMenuForCustomerMeetingStore();
	const fimenu = computed(() => fimenuStore.fimenu);

	//#region PROPS
	const { overlay, pdfRef } = defineProps<IPDFSignatureProps>();
	//#endregion

	//#region COMPUTED
	const displaySignerName = computed(() => {
		const signerId = overlay.signatureMapping.signerId;
		if (signerId === fimenu.value.customer.id) {
			return fimenu.value?.customer?.fullName?.toUpperCase();
		} else if (signerId === fimenu.value.coCustomer.id) {
			return fimenu.value?.coCustomer?.fullName?.toUpperCase();
		} else {
			return fimenu.value.customersInfo.customers.find((c: FIMenuCustomer) => c.id === overlay.signatureMapping.signerId)?.fullName?.toUpperCase();
		}
	});

	const displayImage = computed(() => {
		const signatureIdToApply = overlay.signature.signedSignatureId;
		if (signatureIdToApply) {
			return `${settings.appDomain}/api/SaturnWeb/getSignatureImg?&meetingCode=${$meetingHub.code}&accessKey=${$meetingHub.accessKey}&userId=${$meetingHub.userId}&imgId=${signatureIdToApply}`;
		}
		return null;
	});
	//#endregion

	async function sign() {
		if (!overlay.signature.isSigned) {
			//if we already have an image for this signature, dont open the signaturepad, and just show the signature image
			const personSigning = getPersonSigningFromOverlay(overlay.signatureMapping.signerId, overlay, fimenu.value);
			console.log('signing signature', personSigning, overlay);
			if (personSigning.currentSignatureId) {
				EventBus.emit('clickedToSign', overlay);
			} else {
				// since we dont have a signature fo this signature, open up the signature pad
				EventBus.emit('showSignaturePad', {
					postFunction: onSignaturePadSigned,
					signerId: overlay.signatureMapping.signerId,
				});
			}
		}
	}

	async function onSignaturePadSigned(signature: string, initials: string, signatureData: DigitalSignatureData, initialsData: DigitalSignatureData) {
		console.log('Signature data', signature, initials);
		if (signature && initials) {
			const signatureImages = {
				signatureImgB64: signature,
				initialsImgB64: initials,
			};

			EventBus.emit('signatureReceived', {
				signatureImages,
				overlay,
				signatureData,
				initialsData,
			});
		}
	}

	function calcWidth() {
		if (!pdfRef) return;
		// These are the default values for the All-in-one computer for instore connection
		const DEFAULT_SCREEN_WIDTH = 1903;

		const widthMultiplier = pdfRef.offsetWidth / DEFAULT_SCREEN_WIDTH;
		return overlay.signature.width * widthMultiplier + calcHeight() / 3;
	}

	function calcHeight() {
		// These are the default values for the All-in-one computer for instore connection
		const DEFAULT_SCREEN_HEIGHT = 1080;

		const heightMultiplier = window.innerHeight / DEFAULT_SCREEN_HEIGHT;

		// Height that works for default screen height
		const initialHeight = overlay.signature.height * heightMultiplier * 1.5;

		const innerHeight = window.innerHeight;
		const innerWidth = window.innerWidth;

		const heightToWidthRatio = innerHeight / innerWidth;

		// Setting up Height to Width Ratios range for acceptable signature placement - the window height is affecting the vertical placement of the signature
		const maxHeightToWidthRatio = 0.5;
		const minHeightToWidthRatio = 0.45;

		if (heightToWidthRatio > maxHeightToWidthRatio) {
			const percentDifference = (heightToWidthRatio - maxHeightToWidthRatio) * 100;
			// Formula for calculating how many pixels to reduce height by
			const pixelOffsetToSubtract = 1.2 * percentDifference;
			return initialHeight - pixelOffsetToSubtract;
		} else if (heightToWidthRatio < minHeightToWidthRatio) {
			const percentDifference = (minHeightToWidthRatio - heightToWidthRatio) * 100;
			// Formula for calculating how many pixels to increase height by
			const pixelOffsetToAdd = 1.2 * percentDifference;
			return initialHeight + pixelOffsetToAdd;
		}
		return initialHeight;
	}
</script>

<style>
	@container (max-width: 800px) {
		.sign-name-container {
			position: absolute;
			top: 30px;
		}
	}
	@container (min-width: 801px) and (max-width: 1200px) {
		.sign-name-container {
			position: absolute;
			top: 15px;
		}
	}
	@container (min-width: 1201px) and (max-width: 1920px) {
		.sign-name-container {
			position: absolute;
		}
	}
</style>
